<template>
  <v-dialog
    v-model="dialogOpen"
    width="520"
    content-class="ex-generate-video-ml-dialog"
    @click:outside="onClose"
  >
    <v-card
      class="pa-6 ex-generate-video-ml background-pattern-icon"
      data-testid="ex-generate-video-microlearning-dialog"
    >
      <div class="d-flex align-start justify-space-between generate-ml-header">
        <div class="generate-ml-header-icon" aria-hidden="true">
          <Icon name="generate_microlearning_header" />
        </div>
        <span class="generate-ml-close align-self-start" @click="onClose">
          <v-btn icon color="grey" aria-label="Close">
            <Icon class="cursor-pointer" name="popup_close" />
          </v-btn>
        </span>
      </div>

      <h3 class="generate-ml-title">Generate Microlearning</h3>

      <!-- ── DEFAULT state: pick a video type ── -->
      <template v-if="status === 'idle'">
        <div class="generate-ml-section generate-ml-section--video">
          <p class="generate-ml-section__question">
            What video microlearning you want to generate?
          </p>
          <div class="access-list">
            <div
              v-for="opt in visibleVideoOptions"
              :key="opt.value"
              class="access-item d-flex align-start cursor-pointer"
              :class="{ 'disabled-item': opt.disabled }"
              role="radio"
              :aria-checked="videoType === opt.value"
              tabindex="0"
              @click="videoType = opt.value"
              @keydown.enter.prevent="videoType = opt.value"
              @keydown.space.prevent="videoType = opt.value"
            >
              <div class="generate-ml-option__radio">
                <v-icon v-if="videoType === opt.value" small> $exRadioMiniChecked </v-icon>
                <v-icon v-else small>$exRadioMiniNormal</v-icon>
              </div>
              <div class="generate-ml-option__text">
                <span class="access-name">{{ opt.label }}</span>
                <span class="access-description">
                  <template v-if="opt.value === 'toc'">
                    Ideal for longer videos
                    <span class="access-description__emphasis">with sound</span> that need to be
                    easily navigated
                  </template>
                  <template v-else>
                    Ideal for step by step video instructions
                    <span class="access-description__emphasis">without sound</span> to be seen on a
                    smartphone using the exIQtive mobile app
                  </template>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="generate-ml-footer d-flex">
          <div class="generate-ml-footer__btn">
            <StandardButton
              type="magenta"
              variant="outline"
              size="full-width"
              label="Cancel"
              @click="onClose"
            />
          </div>
          <div class="generate-ml-footer__btn">
            <StandardButton
              type="magenta"
              variant="primary"
              size="full-width"
              label="Generate"
              @click="onGenerate"
            />
          </div>
        </div>
      </template>

      <!-- ── THINKING / FAILED states ── -->
      <div v-else class="generate-ml-section generate-ml-banner">
        <IQThinkingBanner :state="status === 'failed' ? 'failed' : 'thinking'" @retry="onRetry" />
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import IQThinkingBanner from "@components/IQThinkingBanner/IQThinkingBanner.vue";

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  initialVideoType: {
    type: String,
    default: "toc",
    validator: (v) => ["toc", "walkthrough"].includes(v),
  },
  // Generation lifecycle, driven by the parent that triggers the backend
  // and polls/subscribes for completion or failure.
  status: {
    type: String,
    default: "idle",
    validator: (v) => ["idle", "generating", "failed"].includes(v),
  },
});

const emit = defineEmits(["input", "generate", "retry"]);

const videoOptions = [
  {
    value: "toc",
    label: "Video with Table of Contents",
    disabled: false,
  },
  {
    // Hidden until Step by Step Annotated Video Walkthrough is available.
    value: "walkthrough",
    label: "Step by Step Annotated Video Walkthrough",
    disabled: true,
  },
];

const visibleVideoOptions = computed(() => videoOptions.filter((opt) => !opt.disabled));

function resolveVisibleVideoType(requested) {
  const isVisible = videoOptions.some((opt) => opt.value === requested && !opt.disabled);
  return isVisible ? requested : "toc";
}

const videoType = ref(resolveVisibleVideoType(props.initialVideoType));

const dialogOpen = computed({
  get: () => props.value,
  set: (v) => emit("input", v),
});

watch(
  () => props.value,
  (open) => {
    if (open) {
      videoType.value = resolveVisibleVideoType(props.initialVideoType);
    }
  }
);

function onClose() {
  emit("input", false);
}

function onGenerate() {
  emit("generate", {
    videoType: videoType.value,
  });
}

function onRetry() {
  emit("retry", {
    videoType: videoType.value,
  });
}
</script>

<style scoped>
::v-deep(.ex-generate-video-ml-dialog) {
  border-radius: 12px !important;
}

.ex-generate-video-ml {
  border-radius: 12px !important;
  overflow: hidden;
}

.background-pattern-icon {
  background-image: url("@components/assets/backgrounds/background_popup_icon.svg");
  background-size: 216px 216px;
  background-position: 0 0;
}

.generate-ml-close {
  margin: -8px -8px 0 0;
  line-height: 0;
  cursor: pointer;
}

.generate-ml-hero {
  position: relative;
  width: 56px;
  height: 56px;
  flex-shrink: 0;
}

.generate-ml-hero__ring {
  position: absolute;
  border-radius: 50%;
  border: 1px solid #eaecf0;
  pointer-events: none;
}

.generate-ml-hero__ring--outer {
  width: 56px;
  height: 56px;
  left: 0;
  top: 0;
  opacity: 0.55;
}

.generate-ml-hero__ring--mid {
  width: 44px;
  height: 44px;
  left: 6px;
  top: 6px;
  opacity: 0.75;
}

.generate-ml-hero__mint {
  position: absolute;
  left: 10px;
  top: 10px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #d5f5ee;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
}

.generate-ml-hero__star {
  font-size: 8px;
  line-height: 1;
  color: #344054;
  user-select: none;
}

.generate-ml-hero__star--lg {
  font-size: 11px;
}

.generate-ml-title {
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
  margin-top: 8px;
}

.generate-ml-section--video {
  margin-bottom: 20px;
}

.generate-ml-banner {
  margin-top: 24px;
}

.generate-ml-section__question {
  margin: 0 0 18px 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #475467;
}

.generate-ml-option__radio {
  flex-shrink: 0;
  margin-right: 14px;
  padding-top: 1px;
  line-height: 0;
}

.generate-ml-option__text {
  min-width: 0;
  flex: 1;
}

.cursor-pointer {
  cursor: pointer;
}

.disabled-item {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* Options: no card border — selection shown only via radio icons */
.ex-generate-video-ml .access-list .access-item {
  border: none;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  margin: 0;
}

.ex-generate-video-ml .access-list .access-item:not(:last-child) {
  margin-bottom: 12px;
}

.ex-generate-video-ml .access-item .access-name {
  color: var(--colors-text-text-secondary-700, #344054);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.ex-generate-video-ml .access-item .access-description {
  display: block;
  margin-top: 4px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
  color: #475467;
}

.ex-generate-video-ml .access-description__emphasis {
  font-weight: 600;
}

.generate-ml-footer {
  margin-top: 32px;
  gap: 12px;
}

.generate-ml-footer__btn {
  flex: 1;
  min-width: 0;
}
</style>
